import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Search,
  Edit3,
  UserCheck,
  UserX,
  X,
  RefreshCw,
  Users,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import { timeAgo } from '@/components/layout/NotificationBell';

/* ==========================================================================
   S13 — Admin Users

   Manage operators and admins: list, create, edit, toggle active.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

type UserRole = 'operatrice' | 'admin' | 'super_admin';

interface AppUserRecord {
  id: number;
  nome: string;
  cognome: string;
  email: string;
  role: UserRole;
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface UserFormData {
  nome: string;
  cognome: string;
  email: string;
  password: string;
  role: UserRole;
}

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const ROLE_LABELS: Record<UserRole, string> = {
  operatrice: 'Operatrice',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

const ROLE_BADGE_STATUS: Record<
  UserRole,
  'positive' | 'info' | 'neutral'
> = {
  operatrice: 'neutral',
  admin: 'info',
  super_admin: 'positive',
};

const EMPTY_FORM: UserFormData = {
  nome: '',
  cognome: '',
  email: '',
  password: '',
  role: 'operatrice',
};

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function AdminUsers() {
  const toast = useToast();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------

  const [users, setUsers] = useState<AppUserRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUserRecord | null>(null);
  const [formData, setFormData] = useState<UserFormData>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof UserFormData, string>>
  >({});

  // Confirm toggle
  const [pendingToggle, setPendingToggle] = useState<AppUserRecord | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  // ------------------------------------------------------------------
  // Fetch users
  // ------------------------------------------------------------------

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<AppUserRecord[]>('/admin/users');
      setUsers(data ?? []);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Errore di connessione. Riprova.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // ------------------------------------------------------------------
  // Filtered users
  // ------------------------------------------------------------------

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase().trim();
    return users.filter(
      (u) =>
        u.nome.toLowerCase().includes(q) ||
        u.cognome.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [users, searchQuery]);

  // ------------------------------------------------------------------
  // Modal handlers
  // ------------------------------------------------------------------

  const openCreateModal = useCallback(() => {
    setEditingUser(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback((user: AppUserRecord) => {
    setEditingUser(user);
    setFormData({
      nome: user.nome,
      cognome: user.cognome,
      email: user.email,
      password: '',
      role: user.role,
    });
    setFormErrors({});
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  }, []);

  // ------------------------------------------------------------------
  // Form validation
  // ------------------------------------------------------------------

  const validateForm = useCallback((): boolean => {
    const errors: Partial<Record<keyof UserFormData, string>> = {};

    if (!formData.nome.trim()) {
      errors.nome = 'Il nome e obbligatorio';
    }
    if (!formData.cognome.trim()) {
      errors.cognome = 'Il cognome e obbligatorio';
    }
    if (!formData.email.trim()) {
      errors.email = "L'email e obbligatoria";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Inserisci un indirizzo email valido';
    }
    // Password required only on create
    if (!editingUser && !formData.password) {
      errors.password = 'La password e obbligatoria';
    }
    if (formData.password && formData.password.length < 8) {
      errors.password = 'La password deve avere almeno 8 caratteri';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, editingUser]);

  // ------------------------------------------------------------------
  // Save user
  // ------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;
    setIsSaving(true);

    try {
      const payload: Record<string, unknown> = {
        nome: formData.nome.trim(),
        cognome: formData.cognome.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
      };

      // Only send password when it's filled in
      if (formData.password) {
        payload.password = formData.password;
      }

      if (editingUser) {
        const updated = await api.patch<AppUserRecord>(
          `/admin/users/${editingUser.id}`,
          payload,
        );
        setUsers((prev) =>
          prev.map((u) => (u.id === updated.id ? updated : u)),
        );
        toast.success('Utente aggiornato');
      } else {
        const created = await api.post<AppUserRecord>('/admin/users', payload);
        setUsers((prev) => [...prev, created]);
        toast.success('Utente creato');
      }

      closeModal();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message || 'Errore nel salvataggio');
      } else {
        toast.error('Errore di connessione. Riprova.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [validateForm, formData, editingUser, closeModal, toast]);

  // ------------------------------------------------------------------
  // Toggle active
  // ------------------------------------------------------------------

  const confirmToggle = useCallback(async () => {
    if (!pendingToggle) return;
    setIsToggling(true);
    try {
      const updated = await api.patch<AppUserRecord>(
        `/admin/users/${pendingToggle.id}/toggle-active`,
        {},
      );
      setUsers((prev) =>
        prev.map((u) => (u.id === updated.id ? updated : u)),
      );
      toast.success(
        updated.active ? 'Utente riattivato' : 'Utente disattivato',
      );
      setPendingToggle(null);
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message || 'Errore nella modifica');
      } else {
        toast.error('Errore di connessione.');
      }
    } finally {
      setIsToggling(false);
    }
  }, [pendingToggle, toast]);

  // ------------------------------------------------------------------
  // Update form field
  // ------------------------------------------------------------------

  const updateField = useCallback(
    (field: keyof UserFormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (formErrors[field]) {
        setFormErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [formErrors],
  );

  // ------------------------------------------------------------------
  // Available roles based on current user
  // ------------------------------------------------------------------

  const availableRoles: UserRole[] = isSuperAdmin
    ? ['operatrice', 'admin', 'super_admin']
    : ['operatrice'];

  // ------------------------------------------------------------------
  // Loading skeleton
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell header={<Header showBack title="Gestione Utenti" />}>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <UserSkeleton key={i} />
          ))}
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------------

  if (error) {
    return (
      <AppShell header={<Header showBack title="Gestione Utenti" />}>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className={cn(
              'mb-5 flex h-16 w-16 items-center justify-center',
              'rounded-full bg-status-negative-light',
            )}
          >
            <Users className="h-7 w-7 text-status-negative" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
            {error}
          </p>
          <Button
            variant="secondary"
            size="md"
            className="mt-6"
            leftIcon={<RefreshCw strokeWidth={2} />}
            onClick={loadUsers}
          >
            Riprova
          </Button>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------

  return (
    <AppShell
      header={
        <Header
          showBack
          title="Gestione Utenti"
          actions={
            <Button
              size="sm"
              leftIcon={<Plus strokeWidth={2} />}
              onClick={openCreateModal}
            >
              Nuovo
            </Button>
          }
        />
      }
    >
      <div className="space-y-5 pb-8">
        {/* ---- Search ---- */}
        <Input
          placeholder="Cerca per nome, cognome o email..."
          icon={<Search strokeWidth={1.5} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* ---- Stats row ---- */}
        <div className="flex items-center gap-4 text-sm text-text-muted">
          <span>
            <strong className="text-text-primary">{users.length}</strong> utenti totali
          </span>
          <span>
            <strong className="text-text-primary">
              {users.filter((u) => u.active).length}
            </strong>{' '}
            attivi
          </span>
        </div>

        {/* ---- Empty state ---- */}
        {filteredUsers.length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <div
              className={cn(
                'mb-4 flex h-14 w-14 items-center justify-center',
                'rounded-full bg-surface-sunken',
              )}
            >
              <Users className="h-6 w-6 text-text-muted" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-text-muted">
              {searchQuery.trim()
                ? 'Nessun utente trovato per la ricerca'
                : 'Nessun utente configurato'}
            </p>
          </div>
        )}

        {/* ---- User list ---- */}
        <div className="space-y-3">
          {filteredUsers.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              onEdit={() => openEditModal(user)}
              onToggleActive={() => setPendingToggle(user)}
            />
          ))}
        </div>
      </div>

      {/* ---- Create/Edit Modal ---- */}
      {isModalOpen && (
        <UserModal
          isEditing={Boolean(editingUser)}
          formData={formData}
          formErrors={formErrors}
          isSaving={isSaving}
          availableRoles={availableRoles}
          onUpdateField={updateField}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}

      {/* ---- Confirm toggle modal ---- */}
      {pendingToggle && (
        <ConfirmToggleModal
          user={pendingToggle}
          isLoading={isToggling}
          onConfirm={confirmToggle}
          onCancel={() => setPendingToggle(null)}
        />
      )}
    </AppShell>
  );
}

/* ==========================================================================
   Sub-components
   ========================================================================== */

/* --------------------------------------------------------------------------
   User Card
   -------------------------------------------------------------------------- */

function UserCard({
  user,
  onEdit,
  onToggleActive,
}: {
  user: AppUserRecord;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  const fullName = `${user.nome} ${user.cognome}`;
  const badgeStatus = ROLE_BADGE_STATUS[user.role];

  return (
    <div
      className={cn(
        'flex items-center gap-4 px-5 py-4',
        'rounded-[var(--radius-lg)] border bg-surface-card',
        'transition-all duration-[var(--duration-fast)]',
        user.active
          ? 'border-border-default'
          : 'border-border-default opacity-60',
      )}
    >
      {/* Avatar */}
      <Avatar name={fullName} size="md" />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
            {fullName}
          </p>
          <Badge status={badgeStatus} size="sm">
            {ROLE_LABELS[user.role]}
          </Badge>
          {!user.active && (
            <Badge status="negative" size="sm" dot>
              Disattivato
            </Badge>
          )}
        </div>
        <p className="text-xs text-text-muted mt-0.5 truncate">{user.email}</p>
        {user.lastLoginAt && (
          <p className="text-xs text-text-muted mt-0.5">
            Ultimo accesso: {timeAgo(user.lastLoginAt)}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={onEdit}
          className={cn(
            'flex items-center justify-center',
            'h-[var(--touch-min)] w-[var(--touch-min)]',
            'rounded-[var(--radius-md)]',
            'text-text-muted hover:text-text-primary',
            'hover:bg-surface-sunken',
            'transition-colors cursor-pointer',
          )}
          aria-label={`Modifica ${fullName}`}
        >
          <Edit3 className="h-4.5 w-4.5" strokeWidth={1.5} />
        </button>
        <button
          onClick={onToggleActive}
          className={cn(
            'flex items-center justify-center',
            'h-[var(--touch-min)] w-[var(--touch-min)]',
            'rounded-[var(--radius-md)]',
            'transition-colors cursor-pointer',
            user.active
              ? 'text-text-muted hover:text-status-negative hover:bg-status-negative-light'
              : 'text-text-muted hover:text-status-positive hover:bg-status-positive-light',
          )}
          aria-label={user.active ? `Disattiva ${fullName}` : `Riattiva ${fullName}`}
        >
          {user.active ? (
            <UserX className="h-4.5 w-4.5" strokeWidth={1.5} />
          ) : (
            <UserCheck className="h-4.5 w-4.5" strokeWidth={1.5} />
          )}
        </button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   User Modal (Create / Edit)
   -------------------------------------------------------------------------- */

function UserModal({
  isEditing,
  formData,
  formErrors,
  isSaving,
  availableRoles,
  onUpdateField,
  onSave,
  onClose,
}: {
  isEditing: boolean;
  formData: UserFormData;
  formErrors: Partial<Record<keyof UserFormData, string>>;
  isSaving: boolean;
  availableRoles: UserRole[];
  onUpdateField: (field: keyof UserFormData, value: string) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          'fixed inset-0 z-[var(--z-overlay)] bg-surface-overlay',
          'fade-in',
        )}
        onClick={onClose}
        aria-hidden
      />

      {/* Modal sheet */}
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-[var(--z-modal)]',
          'bg-surface-card rounded-t-[var(--radius-xl)]',
          'shadow-xl',
          'px-5 pb-8 pt-5',
          'mx-auto max-w-3xl',
          'max-h-[85dvh] overflow-y-auto',
          'fade-in',
        )}
        role="dialog"
        aria-label={isEditing ? 'Modifica utente' : 'Nuovo utente'}
      >
        {/* Handle */}
        <div className="flex justify-center mb-4">
          <div className="h-1 w-10 rounded-full bg-border-default" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary">
            {isEditing ? 'Modifica Utente' : 'Nuovo Utente'}
          </h3>
          <button
            onClick={onClose}
            className={cn(
              'flex items-center justify-center',
              'h-[var(--touch-min)] w-[var(--touch-min)]',
              'rounded-[var(--radius-md)]',
              'text-text-muted hover:text-text-primary',
              'hover:bg-surface-sunken',
              'transition-colors cursor-pointer',
            )}
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Nome"
              placeholder="Es. Maria"
              value={formData.nome}
              onChange={(e) => onUpdateField('nome', e.target.value)}
              error={formErrors.nome}
            />
            <Input
              label="Cognome"
              placeholder="Es. Rossi"
              value={formData.cognome}
              onChange={(e) => onUpdateField('cognome', e.target.value)}
              error={formErrors.cognome}
            />
          </div>

          <Input
            label="Email"
            type="email"
            placeholder="maria.rossi@ozone.it"
            value={formData.email}
            onChange={(e) => onUpdateField('email', e.target.value)}
            error={formErrors.email}
          />

          <Input
            label={isEditing ? 'Nuova password (lascia vuoto per non cambiare)' : 'Password'}
            type="password"
            placeholder={isEditing ? 'Lascia vuoto per non modificare' : 'Min. 8 caratteri'}
            value={formData.password}
            onChange={(e) => onUpdateField('password', e.target.value)}
            error={formErrors.password}
          />

          {/* Role select */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="user-role"
              className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-secondary"
            >
              Ruolo
            </label>
            <select
              id="user-role"
              value={formData.role}
              onChange={(e) => onUpdateField('role', e.target.value)}
              className={cn(
                'h-[var(--touch-min)] w-full rounded-[var(--radius-md)]',
                'bg-surface-card border border-border-default',
                'px-4 text-base text-text-primary',
                'font-[family-name:var(--font-body)]',
                'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
                'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
                'focus:outline-none',
                'hover:border-border-hover',
              )}
            >
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <Button
            variant="ghost"
            size="lg"
            className="flex-1"
            onClick={onClose}
          >
            Annulla
          </Button>
          <Button
            size="lg"
            className="flex-1"
            loading={isSaving}
            onClick={onSave}
          >
            {isEditing ? 'Salva modifiche' : 'Crea utente'}
          </Button>
        </div>
      </div>
    </>
  );
}

/* --------------------------------------------------------------------------
   Confirm Toggle Modal
   -------------------------------------------------------------------------- */

function ConfirmToggleModal({
  user,
  isLoading,
  onConfirm,
  onCancel,
}: {
  user: AppUserRecord;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const fullName = `${user.nome} ${user.cognome}`;
  const isDeactivating = user.active;

  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          'fixed inset-0 z-[var(--z-overlay)] bg-surface-overlay',
          'fade-in',
        )}
        onClick={onCancel}
        aria-hidden
      />

      {/* Modal */}
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-[var(--z-modal)]',
          'bg-surface-card rounded-t-[var(--radius-xl)]',
          'shadow-xl',
          'px-5 pb-8 pt-5',
          'mx-auto max-w-3xl',
          'fade-in',
        )}
        role="dialog"
        aria-label="Conferma azione"
      >
        {/* Handle */}
        <div className="flex justify-center mb-4">
          <div className="h-1 w-10 rounded-full bg-border-default" />
        </div>

        <div className="text-center mb-6">
          <div
            className={cn(
              'mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full',
              isDeactivating
                ? 'bg-status-negative-light'
                : 'bg-status-positive-light',
            )}
          >
            {isDeactivating ? (
              <UserX
                className="h-7 w-7 text-status-negative"
                strokeWidth={1.5}
              />
            ) : (
              <UserCheck
                className="h-7 w-7 text-status-positive"
                strokeWidth={1.5}
              />
            )}
          </div>
          <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary">
            {isDeactivating ? 'Disattiva utente' : 'Riattiva utente'}
          </h3>
          <p className="text-sm text-text-muted mt-2">
            {isDeactivating
              ? `Vuoi davvero disattivare ${fullName}? L'utente non potra piu accedere al sistema.`
              : `Vuoi riattivare ${fullName}? L'utente potra di nuovo accedere al sistema.`}
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            variant="ghost"
            size="lg"
            className="flex-1"
            onClick={onCancel}
          >
            Annulla
          </Button>
          <Button
            variant={isDeactivating ? 'danger' : 'primary'}
            size="lg"
            className="flex-1"
            loading={isLoading}
            onClick={onConfirm}
          >
            {isDeactivating ? 'Disattiva' : 'Riattiva'}
          </Button>
        </div>
      </div>
    </>
  );
}

/* --------------------------------------------------------------------------
   Skeleton
   -------------------------------------------------------------------------- */

function UserSkeleton() {
  return (
    <div className="flex items-center gap-4 px-5 py-4 rounded-[var(--radius-lg)] border border-border-default bg-surface-card">
      <Skeleton height={40} className="w-10 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton height={14} className="w-2/5" />
        <Skeleton height={12} className="w-3/5" />
      </div>
      <Skeleton height={40} className="w-20 rounded-[var(--radius-md)]" />
    </div>
  );
}
